import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaStore } from "@/hooks/useMediaStore";

export const useCenterElementDetection = (props?: { verticalThreshold?: number }) => {
  const { verticalThreshold = 0.7 } = props || {};
  const isMobileWeb = useMediaStore((store) => store.hoverNone);
  const [centeredElementIds, setCenteredElementIds] = useState<string[]>([]);
  const elementRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isScrollingRef = useRef(false);

  const findCenteredElements = useCallback(() => {
    if (!isMobileWeb) {
      return [];
    }

    const windowHeight = window.innerHeight;
    const centerY = windowHeight / 2;
    const threshold = (windowHeight * verticalThreshold) / 2;

    const newCenteredElementIds: string[] = [];

    Object.entries(elementRefs.current).forEach(([id, ref]: [string, HTMLElement | null]) => {
      if (ref) {
        const rect = ref.getBoundingClientRect();
        const elementCenter = rect.top + rect.height / 2;
        const distance = Math.abs(elementCenter - centerY);

        if (distance < threshold) {
          newCenteredElementIds.push(id);
        }
      }
    });

    return newCenteredElementIds;
  }, [isMobileWeb, verticalThreshold]);

  useEffect(() => {
    if (!isMobileWeb) {
      return;
    }

    const handleScroll = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (!isScrollingRef.current) {
        isScrollingRef.current = true;
        setCenteredElementIds([]);
      }

      timeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
        const newCenteredElementIds = findCenteredElements();
        setCenteredElementIds(newCenteredElementIds);
      }, 1500);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isMobileWeb, findCenteredElements]);

  const setElementRef = useCallback((id: string, ref: HTMLElement | null) => {
    elementRefs.current[id] = ref;
  }, []);

  return {
    centeredElementIds,
    setElementRef,
    elementRefs, // Expose refs for other uses
  };
};
